import type { ImageStatus, ImageSummary } from './api/types';

/**
 * The statuses that end an image's place in a queue without it being finished.
 *
 * Mirrors `_set_aside` in `hismaic/api/tasks.py`, and exists for the same reason it does: two
 * statuses mean "not coming back to this", and every consumer has to treat them alike or the
 * pager offers an image the meter has already written off. One predicate, so a third — if one
 * ever arrives — lands in a single place.
 *
 * They are not the same statement. `skipped` is "I cannot work this photograph", an open
 * question the next annotator may answer. `not_pictured` is "this photograph does not show this
 * inscription", which is settled by looking: it is a panel shot of a neighbouring text on the
 * same rock. See docs/panel_photographs.md.
 */
export const SET_ASIDE: readonly ImageStatus[] = ['skipped', 'not_pictured'];

export function isSetAside(status: ImageStatus): boolean {
  return SET_ASIDE.includes(status);
}

/** Neither finished nor set aside: an image the annotator could still usefully open. */
export function isOpenWork(image: Pick<ImageSummary, 'status'>): boolean {
  return image.status !== 'done' && !isSetAside(image.status);
}

/** Human-readable status, for a table cell or a badge. */
export function statusLabel(status: ImageStatus): string {
  switch (status) {
    case 'unannotated':
      return 'not started';
    case 'in_progress':
      return 'in progress';
    case 'done':
      return 'done';
    case 'skipped':
      return 'skipped';
    case 'not_pictured':
      // Deliberately not "not pictured" on its own, which reads as a broken or missing image
      // and sends somebody chasing the wrong thing. The photograph pictures something — just
      // not this record.
      return 'does not show this record';
  }
}

/**
 * How many other sigla hold this exact photograph, phrased for a person.
 *
 * Null when nothing shares it, so a caller omits the notice rather than printing a reassuring
 * "shared with 0" — the ETag graph is a lower bound, and a zero is not proof that this frame
 * holds one inscription.
 */
export function sharingNotice(sigla: readonly string[]): string | null {
  if (sigla.length === 0) return null;
  const listed =
    sigla.length <= 4
      ? sigla.join(', ')
      : `${sigla.slice(0, 4).join(', ')} and ${sigla.length - 4} more`;
  const plural = sigla.length === 1 ? 'inscription' : 'inscriptions';
  return `Also filed under ${sigla.length} other ${plural} on this rock: ${listed}`;
}
